import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, RefreshCw, ExternalLink, X, ArrowRight, Users } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const STATUS_CHIPS = {
  queued:      { c: "bg-amber-100 text-amber-800",   label: "Queued" },
  in_progress: { c: "bg-sky-100 text-sky-800",       label: "In progress" },
  review:      { c: "bg-violet-100 text-violet-800", label: "In review" },
  delivered:   { c: "bg-emerald-100 text-emerald-800", label: "Delivered" },
  cancelled:   { c: "bg-stone-200 text-stone-700",   label: "Cancelled" },
  failed:      { c: "bg-red-100 text-red-800",       label: "Failed" },
};

const NEXT_ACTIONS = {
  queued:      [{ to: "in_progress", label: "Start" }, { to: "cancelled", label: "Cancel", danger: true }],
  in_progress: [{ to: "review",      label: "Send to review" }, { to: "failed", label: "Fail", danger: true }],
  review:      [{ to: "delivered",   label: "Deliver" }, { to: "in_progress", label: "Send back" }],
  delivered:   [],
  cancelled:   [],
  failed:      [],
};

function StatusChip({ status }) {
  const spec = STATUS_CHIPS[status] || { c: "bg-stone-100 text-stone-700", label: status };
  return (
    <span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-1 rounded-full ${spec.c}`} data-testid={`admin-mkt-status-${status}`}>
      {spec.label}
    </span>
  );
}

function AdvanceDialog({ row, onClose, onDone }) {
  const [to, setTo] = useState("");
  const [notes, setNotes] = useState("");
  const [previewUrl, setPreviewUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const actions = NEXT_ACTIONS[row.status] || [];

  async function submit() {
    if (!to) return;
    setErr(null); setBusy(true);
    try {
      const body = { to_status: to };
      if (notes.trim()) body.notes = notes.trim();
      if (previewUrl.trim()) body.preview_url = previewUrl.trim();
      const { data } = await api.post(
        `/admin/marketplace/queue/${encodeURIComponent(row.provider_project_id)}/advance`,
        body,
      );
      onDone(data.row);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not advance the row.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 grid place-items-center p-4" style={{ zIndex: "var(--z-modal)" }} data-testid="admin-mkt-dialog">
      <div className="bg-white rounded-3xl max-w-lg w-full p-6 shadow-2xl">
        <div className="flex items-start justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-stone-500 font-bold">Advance status</div>
            <div className="mt-1 font-black text-stone-900 text-lg">{row._project?.title || row.project_id}</div>
            <div className="text-[11px] text-stone-500 mt-0.5">
              Current: <StatusChip status={row.status} />
            </div>
          </div>
          <button onClick={onClose} className="text-stone-500 hover:text-stone-900" data-testid="admin-mkt-dialog-close">
            <X size={20} />
          </button>
        </div>

        <div className="mt-4">
          <div className="text-[10px] uppercase tracking-widest text-stone-500 font-bold mb-1.5">New status</div>
          <div className="flex flex-wrap gap-2">
            {actions.length === 0 && (
              <div className="text-[12px] text-stone-500">No further transitions available (terminal state).</div>
            )}
            {actions.map((a) => (
              <button
                key={a.to}
                onClick={() => setTo(a.to)}
                className={`text-[12px] font-bold px-3 py-1.5 rounded-full border transition-colors ${
                  to === a.to
                    ? (a.danger ? "border-red-600 bg-red-600 text-white" : "border-[#E05E36] bg-[#E05E36] text-white")
                    : (a.danger ? "border-red-300 text-red-700 hover:bg-red-50" : "border-stone-300 text-stone-800 hover:bg-stone-50")
                }`}
                data-testid={`admin-mkt-to-${a.to}`}
              >
                {a.label}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-4">
          <label className="text-[10px] uppercase tracking-widest text-stone-500 font-bold">
            Preview URL <span className="text-stone-400">(optional · required to deliver)</span>
          </label>
          <input
            type="url"
            value={previewUrl}
            onChange={(e) => setPreviewUrl(e.target.value)}
            placeholder="https://preview.example.com/demo"
            className="mt-1 w-full rounded-xl border border-stone-200 focus:border-[#E05E36] focus:ring-2 focus:ring-[#E05E36]/20 outline-none p-2.5 text-sm"
            data-testid="admin-mkt-preview-url"
          />
        </div>

        <div className="mt-3">
          <label className="text-[10px] uppercase tracking-widest text-stone-500 font-bold">
            Notes <span className="text-stone-400">(operator log)</span>
          </label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            placeholder="What did you do? Any issues to flag?"
            className="mt-1 w-full rounded-xl border border-stone-200 focus:border-[#E05E36] focus:ring-2 focus:ring-[#E05E36]/20 outline-none p-2.5 text-sm"
            data-testid="admin-mkt-notes"
          />
        </div>

        {err && <div className="mt-3 text-sm text-red-600" data-testid="admin-mkt-error">{err}</div>}

        <div className="mt-5 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 rounded-full text-sm font-bold text-stone-700 hover:bg-stone-100" data-testid="admin-mkt-cancel">
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={busy || !to || (to === "delivered" && !previewUrl.trim())}
            className="inline-flex items-center gap-1.5 px-5 py-2 rounded-full text-sm font-bold text-white bg-[#E05E36] hover:bg-[#c94e2a] disabled:opacity-50"
            data-testid="admin-mkt-confirm"
          >
            {busy ? <Loader2 size={14} className="animate-spin" /> : <ArrowRight size={14} />}
            {busy ? "Advancing…" : "Confirm"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function AdminMarketplace() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [err, setErr] = useState(null);
  const [dialog, setDialog] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const params = filter ? { status: filter } : {};
      const { data } = await api.get("/admin/marketplace/queue", { params });
      setRows(data.queue || []);
    } catch (e) {
      if (e?.response?.status === 401) navigate("/account");
      else if (e?.response?.status === 403) setErr("Admin access required.");
      else setErr(e?.response?.data?.detail || "Could not load queue.");
    } finally {
      setLoading(false);
    }
  }, [filter, navigate]);

  useEffect(() => { load(); }, [load]);

  if (authLoading) return null;
  if (!user) {
    return (
      <div className="max-w-2xl mx-auto p-8 text-center">
        <div className="text-stone-700">Please sign in as an admin to view this page.</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50 pb-24">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-8">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="text-[10px] uppercase tracking-widest font-bold text-[#E05E36]">Admin · Marketplace</div>
            <h1 className="heading text-3xl font-black text-stone-900" data-testid="admin-mkt-h1">
              Manual-relay operator queue
            </h1>
            <p className="mt-1 text-sm text-stone-600">
              Advance status, attach preview URLs, and mirror updates back to customer projects.
            </p>
          </div>
          <button
            onClick={load}
            disabled={loading}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-bold text-stone-800 bg-white border border-stone-200 hover:bg-stone-100 disabled:opacity-50"
            data-testid="admin-mkt-refresh"
          >
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
            Refresh
          </button>
        </div>

        {/* Status filter chips */}
        <div className="mt-5 flex flex-wrap gap-2" data-testid="admin-mkt-filters">
          {[
            ["",            "All"],
            ["queued",      "Queued"],
            ["in_progress", "In progress"],
            ["review",      "In review"],
            ["delivered",   "Delivered"],
            ["cancelled",   "Cancelled"],
            ["failed",      "Failed"],
          ].map(([val, label]) => (
            <button
              key={val || "all"}
              onClick={() => setFilter(val)}
              className={`text-[11px] font-bold px-3 py-1.5 rounded-full border transition-colors ${
                filter === val
                  ? "border-[#E05E36] bg-[#E05E36] text-white"
                  : "border-stone-200 text-stone-700 bg-white hover:bg-stone-100"
              }`}
              data-testid={`admin-mkt-filter-${val || "all"}`}
            >
              {label}
            </button>
          ))}
        </div>

        {err && (
          <div className="mt-5 rounded-2xl border border-red-200 bg-red-50 p-3 text-sm text-red-700" data-testid="admin-mkt-load-error">
            {err}
          </div>
        )}

        <div className="mt-5 rounded-3xl border border-stone-200 bg-white overflow-hidden">
          {loading && rows.length === 0 && (
            <div className="p-12 text-center" data-testid="admin-mkt-loading">
              <Loader2 size={28} className="text-[#E05E36] animate-spin mx-auto" />
              <div className="mt-2 text-sm text-stone-600">Loading queue…</div>
            </div>
          )}
          {!loading && rows.length === 0 && (
            <div className="p-12 text-center" data-testid="admin-mkt-empty">
              <div className="text-stone-700 font-bold">No queue rows{filter ? ` in ${filter}` : ""}.</div>
              <div className="text-[12px] text-stone-500 mt-1">
                Rows appear here as soon as a paid project is dispatched to the manual-relay provider.
              </div>
            </div>
          )}
          {rows.length > 0 && (
            <table className="w-full text-sm" data-testid="admin-mkt-table">
              <thead className="bg-stone-50 border-b border-stone-200">
                <tr className="text-left text-[10px] uppercase tracking-widest text-stone-500 font-bold">
                  <th className="p-3">Project</th>
                  <th className="p-3">Customer</th>
                  <th className="p-3">Type</th>
                  <th className="p-3">Status</th>
                  <th className="p-3">Preview</th>
                  <th className="p-3">Updated</th>
                  <th className="p-3"></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.provider_project_id} className="border-b border-stone-100 hover:bg-stone-50" data-testid={`admin-mkt-row-${r.provider_project_id}`}>
                    <td className="p-3">
                      <div className="font-bold text-stone-900">{r._project?.title || r.project_id}</div>
                      <div className="text-[11px] text-stone-500">{r.provider_project_id}</div>
                    </td>
                    <td className="p-3">
                      <div className="flex items-center gap-1.5 text-[12px] text-stone-700">
                        <Users size={12} className="text-stone-400" />
                        {r._project?.user_id || "—"}
                      </div>
                    </td>
                    <td className="p-3 text-[12px] text-stone-700 capitalize">
                      {(r._project?.project_type || "other").replace(/_/g, " ")}
                    </td>
                    <td className="p-3"><StatusChip status={r.status} /></td>
                    <td className="p-3">
                      {r.preview_url ? (
                        <a href={r.preview_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[12px] text-[#c94e2a] hover:underline">
                          Open <ExternalLink size={11} />
                        </a>
                      ) : (
                        <span className="text-[11px] text-stone-400">—</span>
                      )}
                    </td>
                    <td className="p-3 text-[11px] text-stone-500">
                      {(r.updated_at || r.created_at || "").slice(0, 16).replace("T", " ")}
                    </td>
                    <td className="p-3 text-right">
                      {(NEXT_ACTIONS[r.status] || []).length > 0 ? (
                        <button
                          onClick={() => setDialog(r)}
                          className="text-[12px] font-bold px-3 py-1.5 rounded-full bg-[#E05E36] hover:bg-[#c94e2a] text-white"
                          data-testid={`admin-mkt-advance-${r.provider_project_id}`}
                        >
                          Advance →
                        </button>
                      ) : (
                        <span className="text-[11px] text-stone-400">terminal</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {dialog && (
        <AdvanceDialog
          row={dialog}
          onClose={() => setDialog(null)}
          onDone={(updated) => {
            setDialog(null);
            setRows((rs) => rs.map((r) =>
              r.provider_project_id === updated.provider_project_id
                ? { ...r, ...updated }
                : r,
            ));
          }}
        />
      )}
    </div>
  );
}
